/**
 * Authentication hooks for Centrix API.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError, getCsrfToken } from '../client';
import { clearOnboardingClientState } from '../onboardingState';
import type {
  AuthUser,
  AuthUserRole,
  LoginRequest,
  LoginResponse,
  LoginResult,
  MfaRequiredResponse,
  MfaVerifyRequest,
  CSRFResponse,
} from '@centrix/types';
import { isMfaRequiredResponse } from '@centrix/types';
// Request models generated from the OpenAPI schema (do not edit generated files).
import type { ForgotPassword, ResetPassword } from '../generated';
import type { OnboardingStateResponse } from './useOnboarding';

export type {
  AuthUser,
  AuthUserRole,
  LoginRequest,
  LoginResponse,
  LoginResult,
  MfaRequiredResponse,
  MfaVerifyRequest,
  CSRFResponse,
};
export { isMfaRequiredResponse };
export type { ForgotPassword, ResetPassword };

/** Standard `{ message }` envelope both password-reset endpoints return on success. */
export interface PasswordMessageResponse {
  message: string;
}

export type RegistrantType = 'company' | 'individual';

export interface SignupRequest {
  organisation_name?: string;
  email: string;
  password: string;
  phone_number?: string;
  registration_type: RegistrantType;
}

interface UseCsrfTokenOptions {
  forceRequest?: boolean;
}

/**
 * Fetches the current authenticated user's profile.
 * Returns null when the user is not authenticated (401) — callers should
 * treat null as "not logged in" rather than an error condition.
 */
export function useCurrentUser() {
  return useQuery<AuthUser | null, ApiError>({
    queryKey: ['current-user'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<AuthUser>('/auth/me/');
        return response.data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    // Cache the result (including unauthenticated null) for 5 minutes so that
    // pages visited during the onboarding flow don't fire repeated /me requests
    // before a session has been established.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches the CSRF token required for authentication requests.
 */
export function useCsrfToken(options: UseCsrfTokenOptions = {}) {
  return useQuery<CSRFResponse, ApiError>({
    queryKey: ['csrf-token'],
    queryFn: async () => {
      const csrfToken = await getCsrfToken({ forceRequest: options.forceRequest });
      return { csrfToken };
    },
    staleTime: options.forceRequest ? 0 : 5 * 60 * 1000,
    refetchOnMount: options.forceRequest ? 'always' : undefined,
  });
}

/**
 * Login mutation hook.
 * CSRF token is automatically included by the apiClient interceptor.
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<LoginResult, ApiError, LoginRequest & { csrfToken: string }>({
    mutationFn: async ({ email, password }) => {
      const response = await apiClient.post<LoginResult>('/auth/login/', { email, password });
      return response.data;
    },
    onSuccess: (data) => {
      // When MFA is required no session cookie has been set yet — defer the
      // post-login side effects until /auth/mfa/verify/ completes the login.
      if (isMfaRequiredResponse(data)) return;
      // A new user is authenticating — purge any onboarding/signup state left
      // behind by a previous account on this browser so it cannot leak into
      // this session (e.g. a stale onboarding token forcing the new user into
      // someone else's pending checkout). See onboardingState.ts.
      clearOnboardingClientState();
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
}

/**
 * Completes a password login that returned `mfa_required` by submitting the
 * one-time code (TOTP or backup) against the challenge token. On success the
 * backend sets the auth cookies, so this mirrors the full-login side effects
 * of {@link useLogin}.
 */
export function useMfaVerify() {
  const queryClient = useQueryClient();

  return useMutation<LoginResponse, ApiError, MfaVerifyRequest>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<LoginResponse>('/auth/mfa/verify/', payload);
      return response.data;
    },
    onSuccess: () => {
      clearOnboardingClientState();
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
}

interface LogoutResponse {
  message: string;
}

interface RefreshResponse {
  message: string;
}

/**
 * Signup / account creation mutation hook.
 * Returns OnboardingStateResponse on success, which includes the onboarding token.
 */
export function useSignup() {
  return useMutation<OnboardingStateResponse, ApiError, SignupRequest>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<OnboardingStateResponse>('/auth/register/', payload);
      return response.data;
    },
  });
}

/**
 * Logout mutation hook.
 * Clears JWT cookies on the server.
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<LogoutResponse, ApiError, { csrfToken: string }>({
    mutationFn: async () => {
      const response = await apiClient.post<LogoutResponse>('/auth/logout/');
      return response.data;
    },
    onSuccess: () => {
      // Destroy all client-side onboarding/signup state on exit so the next
      // user to log in on this browser starts from a clean slate.
      clearOnboardingClientState();
      queryClient.clear();
    },
  });
}

/**
 * Refresh token mutation hook.
 * Uses the refresh cookie to get a new access token.
 */
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation<RefreshResponse, ApiError, void>({
    mutationFn: async () => {
      const response = await apiClient.post<RefreshResponse>('/auth/refresh/');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    },
  });
}

/**
 * Requests a password-reset email for the given address.
 * The backend always responds 200 (regardless of whether the email exists)
 * to prevent account enumeration, so callers should show the same neutral
 * confirmation on success.
 */
export function useForgotPassword() {
  return useMutation<PasswordMessageResponse, ApiError, ForgotPassword>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<PasswordMessageResponse>(
        '/auth/password/forgot/',
        payload
      );
      return response.data;
    },
  });
}

/**
 * Completes a password reset using the uid/token pair from the reset email
 * link plus the new password. The token is single-use; the backend returns
 * 400 with `{ uid }` / `{ token }` (or password field errors) when the link is
 * invalid/expired or the password fails the policy.
 */
export function useResetPassword() {
  return useMutation<PasswordMessageResponse, ApiError, ResetPassword>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<PasswordMessageResponse>(
        '/auth/password/reset/',
        payload
      );
      return response.data;
    },
  });
}
